import { MapPositionService } from '../../project-map/shared/map-position.service';
import { Project } from '../interfaces/project.interface';
import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { ProjectGroup } from "../interfaces/project-group.interface";
import { ProjectRouteStation } from "../interfaces/project-route-station.interface";
import { ProjectService } from './project.service';
import { ProjectDataSessionStorageService } from "./project-data-session-storage.service";
import { DataEndChannel } from "../interfaces/data-end-channel.interface";
import { RouteStationService } from "./route-station.service";
import { GroupService } from "./group.service";
import { ImageDataService } from "./image-data.service";
import { ProjectRouteStationWithParam } from "../interfaces/project-route-station.class";
import { DeviceService } from 'src/app/device/shared/services/device.service';
import { SchemaService } from '../../project-schema/shared/services/schema.service';
import { ProjectGroupMetaInformation } from '../interfaces/project-group-meta-information';
import { Scenario } from '../interfaces/scenario.interface';
import { DataEndChannelError } from "../interfaces/data-end-channel-error.interface";
import { ProjectSessionStorageData } from '../interfaces/project-session-storage-data.interface';
import { ProjectMenuData } from "../../project-menu/shared/project-menu-data.interface";
import { EditUser } from 'src/app/user/shared/interfaces/edit-user.interface';
import { UserDataResponse } from 'src/app/user/shared/interfaces/login-data-response.interface';
import { UserDataService } from 'src/app/user/shared/services/user-data.service';

@Injectable({
  providedIn: 'root'
})
export class ProjectDataService {
  private projectSubject$ = new BehaviorSubject<Project[]>([]);
  public projects = this.projectSubject$.asObservable();
  private activeProjectsUsersSubject = new BehaviorSubject<any>([]);
  public activeProjectsUsers = this.activeProjectsUsersSubject.asObservable();
  private projectGroupSubject$ = new BehaviorSubject<ProjectGroup[]>([]);
  public projectGroups = this.projectGroupSubject$.asObservable();
  private projectRouteStationSubject$ = new BehaviorSubject<ProjectRouteStation[]>([]);
  public projectsRouteStations = this.projectRouteStationSubject$.asObservable();
  private projectRouteStationWithParamSubject$ = new BehaviorSubject<ProjectRouteStationWithParam[] | undefined>(undefined);
  public projectsRouteStationsWithParam = this.projectRouteStationWithParamSubject$.asObservable();
  private activeProjectSubject$ = new BehaviorSubject<Project | undefined>(undefined);
  public activeProject = this.activeProjectSubject$.asObservable();
  private activeGroupSubject$ = new BehaviorSubject<ProjectGroup | undefined>(undefined);
  public activeGroup = this.activeGroupSubject$.asObservable();
  private activeRouteStationSubject$ = new BehaviorSubject<ProjectRouteStation | undefined>(undefined);
  public activeRouteStation = this.activeRouteStationSubject$.asObservable();
  private mapPositionDraftSubject$ = new BehaviorSubject<{ latitude: number, longitude: number, target: 'project' | 'group' | 'routeStation' } | undefined>(undefined);
  public mapPositionDraft = this.mapPositionDraftSubject$.asObservable();

  private dataEndChannelSubject$ = new BehaviorSubject<DataEndChannel[]>([]);
  public dataEndChannels = this.dataEndChannelSubject$.asObservable();
  private dataEndChannelErrorsSubject$ = new BehaviorSubject<DataEndChannelError[]>([]);
  public dataEndChannelErrors = this.dataEndChannelErrorsSubject$.asObservable();
  private schemaMetaInformationSubject$ = new BehaviorSubject<ProjectGroupMetaInformation[]>([]);
  public schemaMetaInformation = this.schemaMetaInformationSubject$.asObservable();
  private schemaScenariosSubject$ = new BehaviorSubject<Scenario[]>([]);
  public schemaScenarios = this.schemaScenariosSubject$.asObservable();

  public filteredPorjectsSubject$ = new BehaviorSubject<Project[]>([]);
  public filteredProjects = this.filteredPorjectsSubject$.asObservable();



  constructor(private projectService: ProjectService,
    private groupService: GroupService,
    private routeStationService: RouteStationService,
    private mapPositionService: MapPositionService,
    private projectDataSessionService: ProjectDataSessionStorageService,
    private imageDataService: ImageDataService,
    private deviceService: DeviceService,
    private schemaService: SchemaService,
    private readonly userDataService: UserDataService
  ) { }

  public updateProjects(projects: Project[]): void {
    this.projectSubject$.next(projects);
    this.mapPositionService.setProjectMapPoints(projects)
  }

  public updateProjectsUser(user: EditUser[]): void {
    this.activeProjectsUsersSubject.next(user);
  }

  public updateProjectGroups(projectGroups: ProjectGroup[]): void {
    this.projectGroupSubject$.next(projectGroups);
    let activeProjectData: ProjectSessionStorageData = this.projectDataSessionService.getProjectSessionData();
    this.mapPositionService.setGroupMapPoints(projectGroups, activeProjectData.activeProjectId);
  }

  public updateProjectDataFromSession(sessionData: ProjectMenuData): void {

    this.updateProjects(sessionData.projects);
    this.updateActiveProject(sessionData.activeProject, true);
    this.updateProjectGroups(sessionData.groups);
    this.updateActiveGroup(sessionData.activeGroup, true);
    this.updateProjectRouteStations(sessionData.stations);
    this.updateActiveRouteStation(sessionData.activeStation);
  }

  public updateProjectRouteStations(projectRouteStations: ProjectRouteStation[]): void {
    projectRouteStations.sort((a, b) => {
      if (a.x > b.x) {
        return -1;
      } else if (a.x < b.x) {
        return 1
      } else {
        if (a.y > b.y) {
          return 1
        } else if (a.y < b.y) {
          return -1
        } else {
          return 0
        }
      }
    });

    this.projectRouteStationSubject$.next(projectRouteStations);
    let activeProjectData: ProjectSessionStorageData = this.projectDataSessionService.getProjectSessionData();
    this.mapPositionService.setRouteStationMapPoints(projectRouteStations, activeProjectData.activeProjectGroupId)
    if (projectRouteStations[0]) {
      this.getStationSchemaInfo(projectRouteStations); // TODO: boolean einbauen
    } else {
      this.updateProjectRouteStationsWithParam(undefined);
    }
  }

  public updateProjectRouteStationsWithParam(projectRouteStations: ProjectRouteStationWithParam[] | undefined): void {
    this.projectRouteStationWithParamSubject$.next(projectRouteStations);
  }

  public updateDataEndChannels(dataEndChannels: DataEndChannel[]): void {
    this.dataEndChannelSubject$.next(dataEndChannels);
  }

  public updateDataEndChannelErrors(channelErrors: DataEndChannelError[]): void {
    if (channelErrors.length > 0) {
      this.dataEndChannelErrorsSubject$.next(channelErrors);
    }
    else {
      this.dataEndChannelErrorsSubject$.next([]);
    }
  }

  public updateMapPositionDraft(latitude: number, longitude: number, target: 'project' | 'group' | 'routeStation'): void {
    this.mapPositionDraftSubject$.next({ latitude, longitude, target });
  }

  public updateActiveProject(project: Project | undefined, isFromSession: boolean = false): void {
    if (isFromSession) {
      this.activeProjectSubject$.next(project);
      if (project) {
        this.mapPositionService.updateMapPosition({ latitude: project.latitude, longitude: project.longitude, zoom: 8 });
        if (this.userDataService.isAdmin()) { this.projectService.getProjectsAssignedUsers(project.id).subscribe(activeProjectsUsers => this.updateProjectsUser(activeProjectsUsers)); }


        this.projectService.getAllUnassignedRouteStationImages(project.id).subscribe(images => {
          this.imageDataService.updateUnassignedProjectImages(images);
        });
        this.projectService.getAllRouteStationImages(project.id).subscribe(images => {
          this.imageDataService.updateProjectImages(images);
        });
      } else {
        this.mapPositionService.updateMapPosition({ latitude: 52, longitude: 10, zoom: 5 });
      }
      return;
    }
    this.projectDataSessionService.setActiveProjectId(project ? project.id : "");
    this.updateProjectGroups([]);
    this.updateProjectRouteStations([]);
    this.updateActiveGroup(undefined);
    this.updateActiveRouteStation(undefined);
    this.activeProjectSubject$.next(project);

    if (project) {
      if (this.userDataService.isAdmin()) { this.projectService.getProjectsAssignedUsers(project.id).subscribe(activeProjectsUsers => this.updateProjectsUser(activeProjectsUsers)); }

      this.projectService.getProjectGroups(project.id).subscribe(groups => {
        this.updateProjectGroups(groups);
      });
      this.mapPositionService.updateMapPosition({ latitude: project.latitude, longitude: project.longitude, zoom: 8 });

      this.projectService.getAllUnassignedRouteStationImages(project.id).subscribe(images => {
        this.imageDataService.updateUnassignedProjectImages(images);
      });
      this.projectService.getAllRouteStationImages(project.id).subscribe(images => {
        this.imageDataService.updateProjectImages(images);
      });
    } else {
      this.mapPositionService.updateMapPosition({ latitude: 52, longitude: 10, zoom: 5 });
    }
  }

  public updateActiveGroup(group: ProjectGroup | undefined, isFromSession: boolean = false): void {
    if (isFromSession) {
      this.activeGroupSubject$.next(group);
      if (group) {
        this.mapPositionService.updateMapPosition({ latitude: group.latitude, longitude: group.longitude, zoom: 8 });
      }
      return;
    }
    const isGroupChanged = this.activeGroupSubject$.value?.id !== group?.id;
    this.projectDataSessionService.setActiveProjectGroupId(group ? group.id : "");
    if (isGroupChanged) {
      this.updateActiveRouteStation(undefined);
      this.updateProjectRouteStations([]);
      this.updateProjectRouteStationsWithParam(undefined);
      // Schema-Bündeldaten der vorherigen Gruppe verwerfen, bis die neue Antwort da ist
      this.schemaMetaInformationSubject$.next([]);
      this.schemaScenariosSubject$.next([]);
    }
    this.activeGroupSubject$.next(group);
    if (group === undefined) {
      this.mapPositionService.updateMapPosition({ latitude: 52, longitude: 10, zoom: 5 });
    }
    if (group) {
      this.groupService.getProjectRouteStations(group.id).subscribe(routeStations => {
        this.updateProjectRouteStations(routeStations);
      });
      this.mapPositionService.updateMapPosition({ latitude: group.latitude, longitude: group.longitude, zoom: 8 });
    }
  }

  public updateActiveRouteStation(routeStation: ProjectRouteStation | undefined): void {
    this.activeRouteStationSubject$.next(routeStation);
    this.projectDataSessionService.setActiveProjectRouteStationId(routeStation ? routeStation.id : "");
    if (routeStation) {
      this.routeStationService.getDataEndChannels(routeStation.id).subscribe(dataEndChannels => {
        if (!dataEndChannels) { dataEndChannels = []; }
        this.updateDataEndChannels(dataEndChannels);
      });
      this.deviceService.getChannelErrors(routeStation.samId).subscribe(channelErrors => {

        this.updateDataEndChannelErrors(channelErrors);

      },
        error => {
          this.updateDataEndChannelErrors([]);
        });
    }
  }

  private getStationSchemaInfo(routeStations: ProjectRouteStation[]): void {

    this.schemaService.getSchemaData(routeStations[0].groupId).subscribe(schemaData => {
      this.schemaMetaInformationSubject$.next(schemaData.metaInformations ? schemaData.metaInformations : []);
      this.schemaScenariosSubject$.next(schemaData.scenarios ? schemaData.scenarios : []);
      let stationsWithDeviceParam: ProjectRouteStationWithParam[] = [];
      for (let routeStation of routeStations) {
        let schemaInfo = schemaData.routeStationsSchemaInfo?.find(s => s.samId === routeStation.samId);
        if (schemaInfo) {
          stationsWithDeviceParam.push(new ProjectRouteStationWithParam(routeStation, schemaInfo));
        }
        if (routeStations.length === stationsWithDeviceParam.length) {
          this.updateProjectRouteStationsWithParam(stationsWithDeviceParam);
        }
      }
    });
  }



  public updateFilteredProjects(projects: Project[]): void {
    this.filteredPorjectsSubject$.next(projects);
  }

 public updateActiveProjectById(projectId: number): void {
    const current = this.projectSubject$.getValue();
    const found = current.find(p => p.id === projectId);
    if (found) {
      this.updateActiveProject(found);
    } else {
      this.projectService.getProjects().subscribe(projObj => {
        let project = projObj.find(p => p.id === projectId);
        this.updateActiveProject(project);
        this.updateProjects(projObj);
      });
    }
  }

  /** Lädt die RouteStations (inkl. DataEndChannels) der aktiven Gruppe neu vom Server. */
  public reloadActiveGroupRouteStations(): void {
    const group = this.activeGroupSubject$.value;
    if (group) {
      this.groupService.getProjectRouteStations(group.id).subscribe(routeStations => {
        this.updateProjectRouteStations(routeStations);
      });
    }
  }

  /** Lädt die DataEndChannels der aktiven RouteStation neu vom Server. */
  public reloadActiveRouteStationChannels(): void {
    const station = this.activeRouteStationSubject$.value;
    if (station) {
      this.routeStationService.getDataEndChannels(station.id).subscribe(dataEndChannels => {
        this.updateDataEndChannels(dataEndChannels ?? []);
      });
    }
  }

  public updateActiveGroupById(groupId: number): void {
    const current = this.projectGroupSubject$.getValue();
    const found = current.find(g => g.id === groupId);
    if (found) {
      this.updateActiveGroup(found);
    } else {
      this.groupService.getProjectGroup(groupId).subscribe(grp => {
        this.updateActiveGroup(grp);
        this.updateProjectGroups([...current, grp]);
      });
    }
  }
}
